// Pagina admin /patients/:pseudonym — scheda paziente completa.
// Identità decifrata (audit), storico encounter, note operatore editabili.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AudiqMark, DogEarCard, IconButton, RuleBicolor } from '@audiq/ui';
import { ArrowRight } from 'lucide-react';
import { api, ApiClientError } from '../lib/api';

function fmtDate(s?: string | null) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return s;
  }
}

function fmtDuration(s?: number | null) {
  if (s === null || s === undefined) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `${m}m ${r}s` : `${m}m`;
}

function statusBadge(status: string) {
  const map: Record<string, { label: string; bg: string; fg: string }> = {
    in_progress: { label: 'in corso', bg: 'var(--accent)', fg: '#fff' },
    completed: { label: 'completato', bg: 'var(--primary)', fg: '#fff' },
    abandoned: { label: 'abbandonato', bg: 'var(--mute)', fg: '#fff' },
    aborted_emergency: { label: 'emergenza', bg: 'var(--danger)', fg: '#fff' },
  };
  const m = map[status] ?? { label: status, bg: 'var(--mute)', fg: '#fff' };
  return (
    <span
      className="mono inline-block rounded-md px-2 py-0.5 text-xs"
      style={{ background: m.bg, color: m.fg }}
    >
      {m.label}
    </span>
  );
}

export function PatientDetailPage() {
  const { pseudonym = '' } = useParams<{ pseudonym: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  const detail = useQuery({
    queryKey: ['patient', pseudonym],
    queryFn: () => api.getPatientByPseudonym(pseudonym),
    enabled: pseudonym.length > 0,
  });

  // Sync state notes con dati caricati.
  useEffect(() => {
    if (detail.data) {
      setNotes(detail.data.patient.operator_notes ?? '');
      setNotesDirty(false);
    }
  }, [detail.data]);

  const saveNotes = useMutation({
    mutationFn: () => api.updatePatientNotes(pseudonym, notes),
    onSuccess: () => {
      setNotesDirty(false);
      setSavedAt(new Date());
      queryClient.invalidateQueries({ queryKey: ['patient', pseudonym] });
    },
  });

  if (detail.isLoading) {
    return (
      <p className="text-sm" style={{ color: 'var(--mute)' }}>
        Caricamento…
      </p>
    );
  }
  if (detail.isError) {
    const msg =
      detail.error instanceof ApiClientError && detail.error.payload.code === 'PATIENT_NOT_FOUND'
        ? 'Paziente non trovato.'
        : 'Errore caricamento paziente.';
    return (
      <div
        className="rounded-md border p-4"
        style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
      >
        {msg}
      </div>
    );
  }
  const data = detail.data;
  if (!data) return null;
  const { patient, identity, identity_meta, encounters } = data;

  const totalCritical = encounters.reduce((acc, e) => acc + e.flag_counts.critical, 0);
  const totalEncounters = encounters.length;
  const lastLang = encounters.find((e) => e.languageDetected)?.languageDetected;
  const fullName =
    identity?.first_name || identity?.last_name
      ? `${identity?.first_name ?? ''} ${identity?.last_name ?? ''}`.trim()
      : null;

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mono mb-2" style={{ color: 'var(--mute)' }}>
        <Link to="/patients" style={{ color: 'var(--mute)' }}>
          ← tutti i pazienti
        </Link>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="serif text-3xl" style={{ color: 'var(--ink)' }}>
            {fullName ?? <span className="mono text-2xl">{patient.pseudonym}</span>}
          </h2>
          {fullName ? (
            <p className="mono mt-1 text-xs" style={{ color: 'var(--mute)' }}>
              Pseudonym: {patient.pseudonym}
            </p>
          ) : null}
        </div>
        <AudiqMark size={64} state="idle" />
      </div>

      <RuleBicolor className="my-6" />

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Encounter totali
          </div>
          <div className="serif mt-1 text-3xl" style={{ color: 'var(--ink)' }}>
            {totalEncounters}
          </div>
        </DogEarCard>
        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Red flag (critical)
          </div>
          <div
            className="serif mt-1 text-3xl"
            style={{ color: totalCritical > 0 ? 'var(--danger)' : 'var(--ink)' }}
          >
            {totalCritical}
          </div>
        </DogEarCard>
        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Lingua principale
          </div>
          <div className="serif mt-1 text-3xl" style={{ color: 'var(--ink)' }}>
            {lastLang?.toUpperCase() ?? '—'}
          </div>
        </DogEarCard>
        <DogEarCard
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
            Ultimo accesso
          </div>
          <div className="serif mt-1 text-base" style={{ color: 'var(--ink)' }}>
            {fmtDate(patient.last_seen_at)}
          </div>
        </DogEarCard>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Colonna sinistra: identità + note */}
        <div className="lg:col-span-1">
          <DogEarCard
            className="rounded-md border p-5"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
              Identità
            </h3>
            {identity ? (
              <dl className="grid grid-cols-1 gap-y-2 text-sm">
                {[
                  ['Nome', identity.first_name],
                  ['Cognome', identity.last_name],
                  ['Data di nascita', identity.date_of_birth],
                  ['Codice fiscale', identity.fiscal_code],
                  ['Telefono', identity.phone],
                  ['Email', identity.email],
                ].map(([label, val]) => (
                  <div key={label}>
                    <dt className="mono text-xs" style={{ color: 'var(--mute)' }}>
                      {label}
                    </dt>
                    <dd style={{ color: 'var(--ink)' }}>
                      {val ? val : <em style={{ color: 'var(--mute)' }}>—</em>}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm" style={{ color: 'var(--mute)' }}>
                Nessuna identità raccolta.
              </p>
            )}
            {identity_meta ? (
              <div className="mono mt-4 text-xs" style={{ color: 'var(--mute)' }}>
                Raccolta: {fmtDate(identity_meta.collected_at)}
                <br />
                Metodo: {identity_meta.collection_method}
                <br />
                Ultimo decrypt: {fmtDate(identity_meta.last_decrypted_at)}
              </div>
            ) : null}
          </DogEarCard>

          {/* Note operatore */}
          <DogEarCard
            className="mt-4 rounded-md border p-5"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
              Note operatore
            </h3>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesDirty(true);
              }}
              rows={6}
              placeholder="Note libere su questo paziente. Salvate in chiaro nel DB; ogni modifica è loggata."
              className="w-full rounded-md border px-3 py-2 text-sm outline-none"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
            <div className="mt-2 flex items-center justify-between">
              <div className="mono text-xs" style={{ color: 'var(--mute)' }}>
                {savedAt ? `salvato alle ${fmtDate(savedAt.toISOString())}` : ''}
                {patient.notes_updated_at && !savedAt
                  ? `ultima modifica: ${fmtDate(patient.notes_updated_at)}`
                  : null}
              </div>
              <button
                type="button"
                onClick={() => saveNotes.mutate()}
                disabled={!notesDirty || saveNotes.isPending}
                className="rounded-md px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                {saveNotes.isPending ? 'Salvo…' : 'Salva note'}
              </button>
            </div>
          </DogEarCard>
        </div>

        {/* Colonna destra: storico encounter */}
        <div className="lg:col-span-2">
          <h3 className="serif mb-3 text-xl" style={{ color: 'var(--ink)' }}>
            Storico encounter
          </h3>
          {encounters.length === 0 ? (
            <DogEarCard
              className="rounded-md border p-6 text-center"
              style={{
                background: 'var(--paper)',
                borderColor: 'var(--line)',
                color: 'var(--mute)',
              }}
            >
              Nessun encounter registrato.
            </DogEarCard>
          ) : (
            <div className="space-y-3">
              {encounters.map((e) => (
                <DogEarCard
                  key={e.id}
                  className="rounded-md border p-4"
                  style={{
                    background: 'var(--paper)',
                    borderColor: e.markedClinicalRelevant ? 'var(--accent)' : 'var(--line)',
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        {statusBadge(e.status)}
                        {e.languageDetected ? (
                          <span className="mono text-xs" style={{ color: 'var(--mute)' }}>
                            {e.languageDetected.toUpperCase()}
                          </span>
                        ) : null}
                        {e.reviewedAt ? (
                          <span
                            className="mono inline-block rounded-md border px-2 py-0.5 text-xs"
                            style={{ borderColor: 'var(--primary)', color: 'var(--primary)' }}
                          >
                            ✓ rivisto
                          </span>
                        ) : null}
                        {e.markedClinicalRelevant ? (
                          <span
                            className="mono inline-block rounded-md border px-2 py-0.5 text-xs"
                            style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
                          >
                            ★ rilevante
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-2 mono text-xs" style={{ color: 'var(--mute)' }}>
                        {fmtDate(e.startedAt)} · turni {e.totalTurns} · completezza{' '}
                        {e.completenessPct}%
                        {e.durationSeconds ? ` · ${fmtDuration(e.durationSeconds)}` : ''}
                      </div>
                      {e.flag_counts.critical > 0 || e.outcomes_count > 0 ? (
                        <div className="mt-2 flex gap-3 mono text-xs">
                          {e.flag_counts.critical > 0 ? (
                            <span style={{ color: 'var(--danger)' }}>
                              {e.flag_counts.critical} critical
                            </span>
                          ) : null}
                          {e.flag_counts.warning > 0 ? (
                            <span style={{ color: 'var(--accent)' }}>
                              {e.flag_counts.warning} warning
                            </span>
                          ) : null}
                          {e.outcomes_count > 0 ? (
                            <span style={{ color: 'var(--primary)' }}>
                              {e.outcomes_count} outcome
                            </span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <IconButton
                      as="a"
                      href={`/encounters/${e.id}`}
                      onClick={(ev) => {
                        ev.preventDefault();
                        navigate(`/encounters/${e.id}`);
                      }}
                      icon={ArrowRight}
                      label="Apri encounter"
                      size="md"
                    />
                  </div>
                </DogEarCard>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
